import { Link, useLocation } from "react-router-dom";

// 5-tab sticky bottom navigation for the authenticated /app/* surface. Lives
// inside the centered phone column (NOT pinned to viewport), with a blurred
// paper-50 background and an oxblood active state.

type IconName = "book" | "phone" | "photos" | "family" | "account";

type Tab = {
  label: string;
  to: string;
  icon: IconName;
  match: (pathname: string) => boolean;
};

const TABS: Tab[] = [
  {
    label: "Buch",
    to: "/app",
    icon: "book",
    match: (p) =>
      p === "/app" ||
      p === "/app/" ||
      p.startsWith("/app/chapter") ||
      p.startsWith("/app/storyteller") ||
      p.startsWith("/app/book"),
  },
  {
    label: "Anrufe",
    to: "/app/calls",
    icon: "phone",
    match: (p) => p.startsWith("/app/calls"),
  },
  {
    label: "Fotos",
    to: "/app/photos",
    icon: "photos",
    match: (p) => p.startsWith("/app/photos"),
  },
  {
    label: "Familie",
    to: "/app/family",
    icon: "family",
    match: (p) => p.startsWith("/app/family"),
  },
  {
    label: "Konto",
    to: "/app/account",
    icon: "account",
    match: (p) => p.startsWith("/app/account") || p.startsWith("/app/invoices"),
  },
];

function TabGlyph({ name, active }: { name: IconName; active: boolean }) {
  const sw = active ? 2.1 : 1.6;
  const common = {
    width: 20,
    height: 20,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: sw,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    "aria-hidden": true,
  };
  if (name === "book")
    return (
      <svg {...common}>
        <path d="M4 4h12a3 3 0 013 3v13H7a3 3 0 01-3-3V4z" />
        <path d="M4 4v13a3 3 0 003 3" />
      </svg>
    );
  if (name === "phone")
    return (
      <svg {...common}>
        <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.13.96.36 1.9.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0122 16.92z" />
      </svg>
    );
  if (name === "photos")
    return (
      <svg {...common}>
        <rect x="3" y="5" width="18" height="14" rx="2" />
        <circle cx="9" cy="11" r="2" />
        <path d="M21 17l-5-5L8 19" />
      </svg>
    );
  if (name === "family")
    return (
      <svg {...common}>
        <circle cx="9" cy="8" r="3" />
        <circle cx="17" cy="9" r="2.5" />
        <path d="M3 20c0-3 3-5 6-5s6 2 6 5" />
        <path d="M15 18c.5-1.5 2-3 4-3s3 1 3 3" />
      </svg>
    );
  return (
    <svg {...common}>
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21c0-4 4-7 8-7s8 3 8 7" />
    </svg>
  );
}

export default function AppTabBar() {
  const { pathname } = useLocation();
  return (
    <nav
      aria-label="App-Navigation"
      style={{
        position: "sticky",
        bottom: 0,
        background: "rgba(244,236,216,0.92)",
        backdropFilter: "blur(10px)",
        WebkitBackdropFilter: "blur(10px)",
        borderTop: "1px solid rgba(43,26,15,0.10)",
        padding: "10px 6px 24px",
        display: "flex",
        justifyContent: "space-around",
        marginTop: "auto",
        zIndex: 20,
      }}
    >
      {TABS.map((tab) => {
        const active = tab.match(pathname);
        return (
          <Link
            key={tab.label}
            to={tab.to}
            aria-current={active ? "page" : undefined}
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 3,
              padding: "6px 8px 2px",
              minWidth: 44,
              minHeight: 44,
              color: active ? "var(--oxblood)" : "var(--ink-faint)",
              textDecoration: "none",
              borderTop: active
                ? "1.5px solid var(--oxblood)"
                : "1.5px solid transparent",
              marginTop: -11,
            }}
          >
            <TabGlyph name={tab.icon} active={active} />
            <span
              style={{
                fontFamily: "var(--f-ui)",
                fontSize: 10.5,
                fontWeight: active ? 600 : 500,
                letterSpacing: "0.02em",
              }}
            >
              {tab.label}
            </span>
          </Link>
        );
      })}
    </nav>
  );
}
